import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

import type { Id, UserRoleId } from '@e-socna/data-layer';

import { RightDrawer } from '../../../../components/RightDrawer';
import { SpeedDial } from '../../../../components/SpeedDial';
import { UserGlobalRolesForm } from '../../../../forms/UserGlobalRoles';
import type { UserGlobalRolesFormValues } from '../../../../forms/UserGlobalRoles';

import { useFoapuUserMutation } from '../hooks/useFoapuUserMutation.hook';
import { useInvalidateUserQueries } from '../hooks/useInvalidateUserQueries.hook';

interface Props {
  readonly userId: Id;
  readonly globalRoleIds: readonly UserRoleId[];
  readonly paths: Readonly<{ consult: string; edit: string }>;
}

export const GlobalRolesEdition = ({
  userId,
  globalRoleIds,
  paths,
}: Readonly<Props>) => {
  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { mutate: foapuUserMutation } = useFoapuUserMutation();
  const invalidateUserQueries = useInvalidateUserQueries();

  const initialValues = useMemo(
    () => ({ _id: userId, globalRoleIds }),
    [globalRoleIds, userId],
  );

  const saveGlobalRoles = useCallback(
    (
      values: UserGlobalRolesFormValues,
      handleError: (error: unknown) => unknown,
    ) => {
      foapuUserMutation(
        {
          filter: { _id: userId },
          update: { globalRoleIds: values.globalRoleIds },
        },
        {
          onError: handleError,
          onSuccess: () => {
            leaveEditionMode();
            enqueueSnackbar('Modification enregistrée.');
            invalidateUserQueries(userId);
          },
        },
      );
    },
    [
      enqueueSnackbar,
      foapuUserMutation,
      invalidateUserQueries,
      leaveEditionMode,
      userId,
    ],
  );

  return (
    <>
      <SpeedDial
        actions={[
          {
            id: 'userGlobalRoles',
            icon: <ManageAccountsIcon />,
            name: 'Modifier les rôles globaux',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Autorisations de l'Utilisateur"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <UserGlobalRolesForm
          initialValues={initialValues}
          onCancel={leaveEditionMode}
          onSubmit={saveGlobalRoles}
        />
      </RightDrawer>
    </>
  );
};
